import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Check } from 'lucide-react';
import { useOnboardingTour } from '../../hooks/useOnboardingTour';

// ============================================
// TYPES
// ============================================

interface OnboardingTourProps {
  profileType: 'ADMIN' | 'CUSTOMER' | 'COMPLIANCE' | 'OPERATOR';
  autoStart?: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
}

// ============================================
// CONSTANTS
// ============================================

const TOOLTIP_GAP = 12;
const HIGHLIGHT_PADDING = 8;

// ============================================
// COMPONENT
// ============================================

export function OnboardingTour({ profileType, autoStart = false }: OnboardingTourProps) {
  const {
    tour,
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive,
    next,
    previous,
    skip,
    start,
    shouldShowTour,
  } = useOnboardingTour(profileType);

  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const skipInProgressRef = useRef(false);
  const hasAutoStarted = useRef(false);

  // Auto-start tour on first render if not completed
  useEffect(() => {
    if (autoStart && tour && shouldShowTour(tour.id) && !isActive && !hasAutoStarted.current) {
      hasAutoStarted.current = true;
      const timer = setTimeout(() => start(), 1000);
      return () => clearTimeout(timer);
    }
  }, [autoStart, tour, shouldShowTour, isActive, start]);

  // Find target element and compute positions
  const computePositions = useCallback(() => {
    if (!currentStep) return;

    const el = document.querySelector(currentStep.targetSelector);
    if (!el) {
      // Element not found — auto-skip to next step
      if (!skipInProgressRef.current) {
        skipInProgressRef.current = true;
        // Use setTimeout to avoid calling next() during render
        setTimeout(() => {
          next();
          skipInProgressRef.current = false;
        }, 0);
      }
      return;
    }

    const rect = el.getBoundingClientRect();
    const newTargetRect: TargetRect = {
      top: rect.top - HIGHLIGHT_PADDING,
      left: rect.left - HIGHLIGHT_PADDING,
      width: rect.width + HIGHLIGHT_PADDING * 2,
      height: rect.height + HIGHLIGHT_PADDING * 2,
    };
    setTargetRect(newTargetRect);

    // Compute tooltip position after a tick so tooltipRef has dimensions
    requestAnimationFrame(() => {
      const tooltipEl = tooltipRef.current;
      const tooltipW = tooltipEl?.offsetWidth ?? 320;
      const tooltipH = tooltipEl?.offsetHeight ?? 200;
      const pos = calculateTooltipPosition(
        currentStep.position,
        rect,
        tooltipW,
        tooltipH,
      );
      setTooltipPos(pos);
      setVisible(true);
    });
  }, [currentStep, next]);

  // Recompute on step change or window resize
  useEffect(() => {
    if (!isActive || !currentStep) {
      setVisible(false);
      return;
    }

    skipInProgressRef.current = false;
    setVisible(false);

    // Small delay to let DOM settle (e.g. sidebar animations)
    const timer = setTimeout(computePositions, 150);

    window.addEventListener('resize', computePositions);
    window.addEventListener('scroll', computePositions, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', computePositions);
      window.removeEventListener('scroll', computePositions, true);
    };
  }, [isActive, currentStep, computePositions]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') previous();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isActive, skip, next, previous]);

  if (!isActive || !tour || !currentStep) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Box-shadow overlay with hole around target
  const overlayBoxShadow = targetRect
    ? `0 0 0 9999px rgba(0, 0, 0, 0.65)`
    : 'none';

  return createPortal(
    <div className="fixed inset-0 z-[9999]" aria-live="polite">
      {/* Clickable backdrop to skip */}
      <div
        className="absolute inset-0"
        onClick={skip}
        aria-label="Fechar tour"
      />

      {/* Highlight hole */}
      {targetRect && (
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: overlayBoxShadow,
            transition: 'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease',
            zIndex: 9999,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-label={`Tour: ${currentStep.title}`}
        className="absolute w-80 max-w-[90vw] rounded-xl border border-surface-600 bg-surface-800 shadow-2xl"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          zIndex: 10000,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.25s ease, transform 0.25s ease, top 0.3s ease, left 0.3s ease',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <span className="text-xs font-medium text-primary-400">
            Passo {currentStepIndex + 1} de {totalSteps}
          </span>
          <button
            onClick={skip}
            className="p-1 rounded-md text-surface-400 hover:text-white hover:bg-surface-700 transition-colors"
            aria-label="Pular tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <h3 className="text-base font-semibold text-white mb-1">
            {currentStep.title}
          </h3>
          <p className="text-sm text-surface-300 leading-relaxed">
            {currentStep.description}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-3">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-200"
              style={{
                width: i === currentStepIndex ? 16 : 6,
                height: 6,
                backgroundColor:
                  i === currentStepIndex
                    ? 'rgb(var(--color-primary-400, 99 102 241))'
                    : 'rgb(var(--color-surface-600, 75 85 99))',
              }}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-4 pb-4 gap-2">
          <button
            onClick={skip}
            className="text-xs text-surface-400 hover:text-surface-200 transition-colors"
          >
            Pular tour
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={previous}
              disabled={isFirstStep}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium
                text-surface-300 hover:text-white hover:bg-surface-700
                disabled:opacity-30 disabled:cursor-not-allowed
                transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>

            <button
              onClick={next}
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-medium
                bg-primary-500 hover:bg-primary-400 text-white
                transition-colors"
            >
              {isLastStep ? (
                <>
                  Concluir
                  <Check className="w-4 h-4" />
                </>
              ) : (
                <>
                  Próximo
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ============================================
// POSITIONING HELPER
// ============================================

function calculateTooltipPosition(
  position: 'top' | 'bottom' | 'left' | 'right',
  targetRect: DOMRect,
  tooltipW: number,
  tooltipH: number,
): TooltipPosition {
  let top = 0;
  let left = 0;

  switch (position) {
    case 'bottom':
      top = targetRect.bottom + TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tooltipW / 2;
      break;
    case 'top':
      top = targetRect.top - tooltipH - TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tooltipW / 2;
      break;
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
      left = targetRect.right + TOOLTIP_GAP;
      break;
    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
      left = targetRect.left - tooltipW - TOOLTIP_GAP;
      break;
  }

  // Clamp to viewport
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (left < 8) left = 8;
  if (left + tooltipW > vw - 8) left = vw - tooltipW - 8;
  if (top < 8) top = 8;
  if (top + tooltipH > vh - 8) top = vh - tooltipH - 8;

  return { top, left };
}

export default OnboardingTour;
